import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Lock } from "lucide-react";
import { profileService, type LegalEntityHierarchy } from "@/services/profileService";
import { crmIntegrationConfigsService } from "@/services/crmIntegrationConfigsService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";
import { useSettingsStore } from "@/store/settingsStore";
import { flattenEntities, findEntityById } from "@/modules/settings/entityHelpers";
import { findRootLegalEntityForSelection } from "@/modules/subscription/billingAccess";
import { cn } from "@/lib/cn";

const PROPCO_ID = "PROPCO";
const REAPIT_ID = "REAPIT";
const CREDENTIAL_CRM_IDS = new Set([PROPCO_ID, REAPIT_ID]);

function scopeToSelectedOrg(
  legalEntities: LegalEntityHierarchy[],
  legalEntityId: string | null | undefined,
): LegalEntityHierarchy[] {
  if (!legalEntityId) return legalEntities;
  const selected = findEntityById(legalEntities, legalEntityId);
  return selected ? [selected] : legalEntities;
}

/** Ref-mapped CRMs (Alto, etc.). Propco and Reapit are credential-based, not ref-mapped. */
function toRefMappedCrmId(crm?: string | null): string | null {
  if (!crm?.trim()) return null;
  const normalized = crm.trim().toUpperCase();
  if (CREDENTIAL_CRM_IDS.has(normalized)) return null;
  return normalized;
}

function findRefMappedCrm(entities: LegalEntityHierarchy[]): string | null {
  for (const le of flattenEntities(entities)) {
    const leCrm = toRefMappedCrmId(le.sourceCrm);
    if (leCrm) return leCrm;
    for (const branch of le.branches ?? []) {
      const branchCrm = toRefMappedCrmId(branch.sourceCrm);
      if (branchCrm) return branchCrm;
    }
  }
  return null;
}

async function findInstalledCredentialCrm(
  profileEntities: LegalEntityHierarchy[],
  scopedEntities: LegalEntityHierarchy[],
  legalEntityId: string | null | undefined,
): Promise<string | null> {
  const root = findRootLegalEntityForSelection(profileEntities, legalEntityId ?? null, null);
  if (Boolean(root?.username?.trim())) return PROPCO_ID;

  const scopedIds = flattenEntities(scopedEntities).map((le) => le.id);
  const configResults = await Promise.allSettled(
    scopedIds.map((id) => crmIntegrationConfigsService.list(id)),
  );
  const active = configResults
    .flatMap((result) =>
      result.status === "fulfilled" && Array.isArray(result.value) ? result.value : [],
    )
    .find(
      (config) =>
        CREDENTIAL_CRM_IDS.has(config.crmType?.trim().toUpperCase() ?? "") &&
        config.isActive === true,
    );
  return active?.crmType ? active.crmType.trim().toUpperCase() : null;
}

interface CrmCardProps {
  id: string;
  name: string;
  description: string;
  logo: string;
  installed?: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
}

const CrmCard: React.FC<CrmCardProps> = ({
  id,
  name,
  description,
  logo,
  installed,
  disabled,
  comingSoon,
}) => {
  const navigate = useNavigate();
  const isInteractive = !disabled && !comingSoon;

  return (
    <div
      onClick={() => isInteractive && navigate(`/integrations/${id}`)}
      className={cn(
        "bg-white rounded-2xl p-5 border transition-all flex flex-col h-full relative",
        comingSoon &&
          "cursor-not-allowed border-bridge-border bg-slate-50/60",
        disabled &&
          !comingSoon &&
          "opacity-50 grayscale cursor-not-allowed border-bridge-border",
        isInteractive &&
          "border-bridge-border hover:shadow-xl hover:border-bridge-accent/30 cursor-pointer group",
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-bridge-bg p-1.5 flex items-center justify-center text-xl">
          {logo}
        </div>
        {installed && !comingSoon && (
          <div className="flex items-center gap-1 px-2.5 py-0.5 bg-green-50 text-green-600 rounded-full text-[10px] font-bold border border-green-100">
            <CheckCircle2 className="w-3 h-3" />
            Installed
          </div>
        )}
        {comingSoon && (
          <div className="px-2.5 py-1 rounded-full bg-slate-200/80 text-[10px] font-bold uppercase tracking-wide text-slate-500 border border-slate-200">
            Coming soon
          </div>
        )}
        {disabled && !comingSoon && (
          <div className="flex items-center gap-1 px-2.5 py-0.5 bg-slate-50 text-slate-400 rounded-full text-[10px] font-bold border border-slate-100">
            <Lock className="w-3 h-3" />
            Locked
          </div>
        )}
      </div>
      <h3
        className={cn(
          "text-base font-semibold text-bridge-fg mb-1.5 transition-colors",
          isInteractive && "group-hover:text-bridge-accent",
        )}
      >
        {name}
      </h3>
      <p className="text-xs text-bridge-muted leading-relaxed flex-1">{description}</p>
    </div>
  );
};

export const CrmIntegrationList: React.FC = () => {
  const { keycloak } = useKeycloak();
  const { legalEntityId } = useSettingsStore();
  const email = keycloak?.tokenParsed?.email as string | undefined;
  const [activeCrm, setActiveCrm] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    void profileService.get(email, legalEntityId).then(async (p) => {
      if (cancelled) return;
      const profileEntities = Array.isArray(p?.legalEntities) ? p.legalEntities : [];
      const scopedEntities = scopeToSelectedOrg(profileEntities, legalEntityId);
      const refMappedCrm = findRefMappedCrm(scopedEntities);
      // Only query credential-CRM status if no ref-mapped CRM exists.
      let credentialCrm: string | null = null;
      if (!refMappedCrm) {
        credentialCrm = await findInstalledCredentialCrm(
          profileEntities,
          scopedEntities,
          legalEntityId,
        );
      }
      // Safety check after async call to prevent updating state on unmounted component.
      if (cancelled) return;
      setActiveCrm(refMappedCrm ?? credentialCrm);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [email, legalEntityId]);

  const crms = [
    {
      id: REAPIT_ID,
      name: "Reapit",
      description: "Reapit software helps property agencies of all sizes to build relationships and grow their businesses.",
      logo: "🏠",
    },
    {
      id: "ALTO",
      name: "Alto",
      description: "More estate agent brands choose Alto to help run their business than any other property CRM on the market.",
      logo: "☁️",
    },
    {
      id: "PROPCO",
      name: "Propco",
      description:
        "Propco links your property management data to Bridge AI with a single account shared across every legal entity and branch.",
      logo: "🔐",
    },
    {
      id: "APEX27",
      name: "Apex27",
      description:
        "Apex27 is a fresh, new, CRM solution specifically designed for estate agent professionals in the ever-changing landscape",
      logo: "27",
      comingSoon: true,
    },
  ];

  // Once a CRM is mapped the list narrows to it.
  const visibleCrms = activeCrm ? crms.filter(c => c.id === activeCrm) : crms;

  if (loading) return <div className="h-40 flex items-center justify-center animate-pulse bg-slate-50 rounded-2xl" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleCrms.map((crm) => (
          <CrmCard
            key={crm.id}
            {...crm}
            installed={crm.id === activeCrm}
          />
        ))}
      </div>

      <div className="bg-bridge-accent/5 border border-bridge-accent/10 rounded-2xl p-6 flex flex-col items-center text-center">
        <h3 className="text-base font-semibold text-bridge-fg mb-1.5">Can't find what you need?</h3>
        <p className="text-xs text-bridge-muted mb-4">You can submit your request and influence which integration we will develop next!</p>
        <button className="px-5 py-2 bg-bridge-fg text-bridge-bg rounded-lg font-semibold text-xs hover:opacity-90 transition-opacity">
          Submit A Request
        </button>
      </div>
    </div>
  );
};
